import { TrashIcon } from '@heroicons/react/24/outline';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import FieldsetContainer from '@/components/shared/FieldsetContainer';
import ConditionalRenderer from '@/components/ui/custom/ConditionalRenderer';
import { Badge, Button, Select, TextInput } from '@/components/ui/daisy';

import { useConfirm } from '@/data/context/ConfirmModalContext';
import { ActivityBlockType } from '@/models/Activity';
import { AssessmentItemTypeEnum } from '@/models/Assessment';
import {
  AssessmentCriteriaField,
  AssessmentItemFormFields,
} from './AssessmentItemForm';

type AssessmentCriteriaItemProps = {
  index: number;
  maxAvailable: number;
  onDelete: () => void;
  criterion: AssessmentCriteriaField;
  usedActivityTypes: Set<ActivityBlockType>;
};

export default function AssessmentCriteriaItem({
  index,
  criterion,
  maxAvailable,
  onDelete,
  usedActivityTypes,
}: AssessmentCriteriaItemProps) {
  const { t } = useTranslation('translation', {
    keyPrefix: 'courseLineForm.evaluationsTab',
  });
  const { t: tActivityBlockTypes } = useTranslation('translation', {
    keyPrefix: 'activityBlockTypes',
  });

  const confirmDelete = useConfirm();

  const {
    register,
    trigger,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<AssessmentItemFormFields>();

  const itemErrors = errors?.criteria?.[index];
  const [parentType, currentActivityType] = watch([
    'type',
    `criteria.${index}.activityType`,
  ]);
  const isDigitalActivity =
    parentType === AssessmentItemTypeEnum.DIGITAL_ACTIVITY;

  const isOptionDisabled = (option: ActivityBlockType) =>
    usedActivityTypes.has(option) && currentActivityType !== option;

  const handleDelete = () => {
    if (criterion.id)
      confirmDelete({
        onConfirm: onDelete,
      });
    else onDelete();
  };

  const maxAvailableNumber = maxAvailable || 0;

  const rangeMessage =
    maxAvailableNumber === 0
      ? t('criteriaMaxGradeZero')
      : t('criteriaMaxGradeRange', { max: maxAvailableNumber });

  return (
    <FieldsetContainer data-testid="criteria-row" block className="gap-4">
      <div className="grid grid-cols-2 gap-4">
        <ConditionalRenderer condition={!isDigitalActivity}>
          <TextInput.Fieldset
            label={t('criteriaName')}
            labelAlt={itemErrors?.name?.message}
          >
            <TextInput
              type="text"
              placeholder={t('criteriaNamePlaceholder')}
              className="w-full"
              color={itemErrors?.name?.message ? 'error' : 'default'}
              {...register(`criteria.${index}.name`, {
                required: t('criteriaNameRequired'),
                onChange: () => {
                  setValue(`criteria.${index}.activityType`, '', {
                    shouldDirty: true,
                  });
                },
              })}
            />
          </TextInput.Fieldset>
        </ConditionalRenderer>

        <ConditionalRenderer condition={isDigitalActivity}>
          <Select.Fieldset
            label={t('criteriaActivityType')}
            labelAlt={itemErrors?.activityType?.message}
          >
            <Select
              className="w-full"
              color={itemErrors?.activityType?.message ? 'error' : 'default'}
              {...register(`criteria.${index}.activityType`, {
                required: isDigitalActivity
                  ? t('criteriaActivityTypeRequired')
                  : false,
                onChange: event => {
                  const value = event.target.value as ActivityBlockType;
                  setValue(
                    `criteria.${index}.name`,
                    value ? tActivityBlockTypes(value) : '',
                    { shouldDirty: true, shouldValidate: true },
                  );
                },
              })}
            >
              <Select.Option value="" disabled>
                {t('criteriaActivityTypePlaceholder')}
              </Select.Option>
              <Select.Option
                value={ActivityBlockType.REGULAR}
                disabled={isOptionDisabled(ActivityBlockType.REGULAR)}
              >
                {tActivityBlockTypes(ActivityBlockType.REGULAR)}
              </Select.Option>
              <Select.Option
                value={ActivityBlockType.OPTIONAL}
                disabled={isOptionDisabled(ActivityBlockType.OPTIONAL)}
              >
                {tActivityBlockTypes(ActivityBlockType.OPTIONAL)}
              </Select.Option>
              <Select.Option
                value={ActivityBlockType.CONSOLIDATION}
                disabled={isOptionDisabled(ActivityBlockType.CONSOLIDATION)}
              >
                {tActivityBlockTypes(ActivityBlockType.CONSOLIDATION)}
              </Select.Option>
            </Select>
          </Select.Fieldset>
        </ConditionalRenderer>

        <TextInput.Fieldset
          label={t('criteriaMaxGrade')}
          labelAlt={itemErrors?.maxGrade?.message}
        >
          <TextInput.Wrapper
            color={itemErrors?.maxGrade?.message ? 'error' : 'default'}
            className="w-full"
          >
            <TextInput
              type="number"
              placeholder={t('criteriaMaxGradePlaceholder')}
              onWheel={event => event.currentTarget.blur()}
              {...register(`criteria.${index}.maxGrade`, {
                required: t('criteriaMaxGradeRequired'),
                valueAsNumber: true,
                onChange: () => {
                  if (errors.criteria || errors.maxGrade) trigger('criteria');
                },
                min: {
                  value: 0,
                  message: rangeMessage,
                },
                max: {
                  value: maxAvailableNumber,
                  message: rangeMessage,
                },
              })}
            />
            <TextInput.Addon>
              <Badge size="xs" variant="soft">
                {t('criteriaMaxGradeMaxBadge', { max: maxAvailableNumber })}
              </Badge>
            </TextInput.Addon>
          </TextInput.Wrapper>
        </TextInput.Fieldset>
      </div>

      <TextInput.Fieldset label={t('criteriaDescription')}>
        <TextInput
          type="text"
          placeholder={t('criteriaDescriptionPlaceholder')}
          className="w-full"
          {...register(`criteria.${index}.description`)}
        />
      </TextInput.Fieldset>

      <Button
        data-testid="criteria-delete-button"
        type="button"
        variant="soft"
        color="error"
        size="xs"
        className="self-end"
        onClick={handleDelete}
      >
        <TrashIcon className="w-3 h-3" />
        {t('deleteCriteria')}
      </Button>
    </FieldsetContainer>
  );
}
